import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import { GPX_FEATURES, GPX_CAPABILITIES } from '@rsos/gainsight';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { setOutboundSMSLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { dismissRcsToast } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { useLazyGetConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { endConversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { RootState } from '../../../app';
import {
  BODY_APPLICATION__TEXT_FROM_911,
  BODY_SOURCE__OUTBOUND_SMS,
  TYPE__LOCATION,
  TYPE_SERVER_SENT_EVENT,
} from '../../../constants/hermes';
import { trackEndChatFromServer } from '../websocketsTrackings';

// Note: Despite the name, this hook handles all text-based 911 features including TF911 (Text-From-911), and RCS
const CONVERSATION_END = 'conversation.end';
const CONVERSATION_UPDATE = 'conversation.update';
const CONVERSATION_NEW_MESSAGE = 'conversation.new_message';

const outboundSMSLocationSchema = z.object({
  type: z.literal(TYPE__LOCATION),
  body: z
    .object({
      application: z.string().optional(),
      source: z.string().optional(),
    })
    .passthrough(),
});

const serverSentEventSchema = z.object({
  type: z.literal(TYPE_SERVER_SENT_EVENT),
  body: z.object({
    caller_id: z.string(),
    events: z
      .array(
        z.object({
          event_type: z.string(),
          metadata: z
            .object({
              conversation_id: z.string().optional(),
            })
            .optional(),
        }),
      )
      .min(1),
  }),
});

const getFeature = (
  conversationId: string | undefined,
  isOutbound: boolean,
): string => {
  if (!conversationId) return GPX_FEATURES.TF911;
  return isOutbound ? GPX_FEATURES.TF911_OUTBOUND : GPX_FEATURES.TF911;
};

const useHandleTextFrom911LocationMessages = () => {
  const Hermes = window?.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const hasAccess = useIsTF911Enabled();

  const selectedCallerID = useSelector(
    (state: RootState) => state?.emergencyCalls?.selectedCallerID,
  );

  const tf911conversations = useSelector(
    (state: RootState) => state?.textFrom911?.conversations,
  );

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const [triggerTF911] = useLazyGetConversationMetadataQuery();

  const handleOutboundSMSMessages = useCallback(
    (message: Record<string, unknown>) => {
      const parsed = outboundSMSLocationSchema.safeParse(message);
      if (!parsed.success) return;

      const { body } = parsed.data;
      if (
        body.application === BODY_APPLICATION__TEXT_FROM_911 ||
        body.source === BODY_SOURCE__OUTBOUND_SMS
      ) {
        // It is an outbound SMS location message
        dispatch(setOutboundSMSLocation(body));
      }
    },
    [dispatch],
  );

  const handleServerSentEventMessages = useCallback(
    (message: Record<string, unknown>) => {
      const parsed = serverSentEventSchema.safeParse(message);
      if (!parsed.success) return;

      const { caller_id: callerID, events } = parsed.data.body;
      const eventType = events[0].event_type;
      const conversationId = events[0].metadata?.conversation_id;

      const conversationStatuses = [
        CONVERSATION_END,
        CONVERSATION_UPDATE,
        CONVERSATION_NEW_MESSAGE,
      ];

      if (!conversationStatuses.includes(eventType)) return;

      const isOutbound =
        tf911conversations?.[callerID]?.data?.isOutboundSMS ?? false;
      const feature = getFeature(conversationId, isOutbound);

      if (eventType === CONVERSATION_END) {
        dispatch(endConversation(callerID));

        trackEndChatFromServer({
          feature,
          capability: GPX_CAPABILITIES.TF911,
          conversationId: conversationId ?? '',
        });
      }

      // if the conversation is updated, only clear unseenMessage flag if user is actually viewing the RCS chat
      if (eventType === CONVERSATION_UPDATE) {
        triggerTF911({ callerID, orgName });

        dispatch(dismissRcsToast({ callerID }));
      }

      // If this conversation is with the selectedCallerID, fetch its metadata
      // to trigger rerendering of chat components
      if (selectedCallerID === callerID) {
        if (selectedCallerID === null) {
          throw new Error(
            'selectedCallerID is unexpectedly null. Cannot fetch metadata to trigger chat rerender.',
          );
        }
        triggerTF911({ callerID: selectedCallerID, orgName });
      }
    },
    [dispatch, orgName, selectedCallerID, triggerTF911, tf911conversations],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({
        ...(hasAccess
          ? { handleOutboundSMSMessages: handleOutboundSMSMessages }
          : {}),
        handleServerSentEventMessages: handleServerSentEventMessages,
      });

      return () => {
        Hermes.removeOnMessageCallback('handleOutboundSMSMessages');
        Hermes.removeOnMessageCallback('handleServerSentEventMessages');
      };
    }
  }, [
    Hermes,
    handleOutboundSMSMessages,
    handleServerSentEventMessages,
    hasAccess,
  ]);

  return null;
};

export default useHandleTextFrom911LocationMessages;
